import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SubTitle from "../SubTitle";
import AppService from "../../Configurations/Appservice";

// Below this threshold we ask the user to reconfirm — a common slip is typing
// the THAKLIS share (e.g. 20) into a field that actually wants the PARTNER share.
const LOW_PARTNER_SHARE_WARN_THRESHOLD = 90;

/**
 * Reusable section for editing a service partner's profit-share agreement.
 * Used inside the partner organisation create / edit pages.
 *
 * The component is uncontrolled at the API layer — call `loadForOrganisation(id)`
 * once the org id is known to fetch existing values, and `save(orgId)` to upsert.
 * Pass `ref` to invoke save imperatively from the parent's submit handler.
 *
 * Profit-share semantics: the percentage stored here is what the PARTNER keeps
 * out of (serviceCharge + tax); Thaklis takes the remainder. Matches the
 * channel-partner commission convention.
 */
const ServicePartnerOnboardingSection = React.forwardRef(({ organisationId, embedded = true }, ref) => {
  const [profitSharePercentage, setProfitSharePercentage] = useState("");
  const [agreementSignedDate, setAgreementSignedDate] = useState(null);
  const [agreementDocumentUrl, setAgreementDocumentUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);

  // Low-share confirmation dialog state. We stash the Promise resolver here so
  // the imperative save() can block on the user's choice before continuing.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPct, setPendingPct] = useState(null);
  const confirmResolverRef = useRef(null);

  // Load existing onboarding row when org id becomes known (edit flow).
  useEffect(() => {
    if (!organisationId) return;
    AppService.get(`v1/service-partner-onboarding/${organisationId}`)
      .then((resp) => {
        if (!resp) return; // 200 with null body when no row exists yet
        if (resp.profitSharePercentage != null) setProfitSharePercentage(String(resp.profitSharePercentage));
        // Backend serializes LocalDate as "YYYY-MM-DD"; new Date(string) parses it as local time.
        if (resp.agreementSignedDate) setAgreementSignedDate(new Date(resp.agreementSignedDate));
        if (resp.agreementDocumentUrl) setAgreementDocumentUrl(resp.agreementDocumentUrl);
        if (resp.notes) setNotes(resp.notes);
      })
      .catch(() => {
        // 404 / no row yet — keep form empty
      });
  }, [organisationId]);

  // Format JS Date as "YYYY-MM-DD" in local time (avoid UTC drift around midnight).
  const toIsoDate = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Wraps the dialog flow in a Promise so save() can `await` the user's choice.
  // Resolves true on Confirm, false on Cancel.
  const askLowShareConfirmation = (pct) =>
    new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setPendingPct(pct);
      setConfirmOpen(true);
    });

  const closeConfirm = (result) => {
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setConfirmOpen(false);
    setPendingPct(null);
    if (resolve) resolve(result);
  };

  React.useImperativeHandle(ref, () => ({
    /**
     * Pre-flight validation + low-share confirmation. Returns true when it's
     * OK to proceed with the parent's submit flow, false when the user cancelled
     * the confirmation dialog. Must be called BEFORE any sibling API calls so
     * that "cancel" can abort the whole submit cleanly (no half-applied writes).
     */
    validate: async () => {
      setError(null);
      const pct = parseFloat(profitSharePercentage);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setError("Profit share % must be between 0 and 100");
        return false;
      }
      // Guard against the user accidentally entering Thaklis's share instead
      // of the partner's. 90% is the typical floor for partner share; below
      // that we want an explicit confirmation.
      if (pct < LOW_PARTNER_SHARE_WARN_THRESHOLD) {
        const confirmed = await askLowShareConfirmation(pct);
        if (!confirmed) return false;
      }
      return true;
    },
    /**
     * Persist the onboarding row. Assumes validate() was called and returned true.
     */
    save: async (orgId) => {
      const pct = parseFloat(profitSharePercentage);
      const payload = {
        profitSharePercentage: pct,
        // Backend expects ISO "YYYY-MM-DD" for LocalDate.
        agreementSignedDate: toIsoDate(agreementSignedDate),
        agreementDocumentUrl: agreementDocumentUrl || null,
        notes: notes || null,
      };
      return AppService.post(`v1/service-partner-onboarding/${orgId}`, payload);
    },
    /** True when the user has entered a percentage (used to gate the parent submit button). */
    isFilled: () => {
      const pct = parseFloat(profitSharePercentage);
      return !Number.isNaN(pct) && pct >= 0 && pct <= 100;
    },
    reset: () => {
      setProfitSharePercentage("");
      setAgreementSignedDate(null);
      setAgreementDocumentUrl("");
      setNotes("");
      setError(null);
    },
  }));

  const inner = (
    <>
      <Box mb={2}>
        <SubTitle fontSize={14}>Service Partner Agreement</SubTitle>
        <Typography variant="caption" color="text.secondary">
          The percentage of (service charge + tax) the partner keeps on each ticket. Thaklis retains the rest.
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Profit Share % *"
            type="number"
            fullWidth
            value={profitSharePercentage}
            onChange={(e) => setProfitSharePercentage(e.target.value)}
            // Stop the browser's default "scroll-wheel changes number" behavior
            // — it's too easy to accidentally bump the value after typing.
            onWheel={(e) => e.target.blur()}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0, max: 100, step: 0.01 },
            }}
            helperText="Partner's share of service charge + tax"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Agreement Signed Date"
              value={agreementSignedDate}
              onChange={(d) => setAgreementSignedDate(d)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Agreement Document URL"
            fullWidth
            value={agreementDocumentUrl}
            onChange={(e) => setAgreementDocumentUrl(e.target.value)}
            placeholder="https://..."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => closeConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "#d32f2f" }}>Confirm Low Partner Share</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            You entered <b>{pendingPct}%</b> as the partner's share.
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            This means the partner keeps <b>{pendingPct}%</b> and Thaklis takes <b>{pendingPct != null ? (100 - pendingPct).toFixed(2) : "—"}%</b> of (service charge + tax).
          </Alert>
          <DialogContentText sx={{ fontSize: "0.85rem" }}>
            A common mistake is typing Thaklis's share here. Continue only if this is really the partner's share.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirm(false)}>Cancel</Button>
          <Button onClick={() => closeConfirm(true)} color="warning" variant="contained">
            Yes, partner keeps {pendingPct}%
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return embedded ? <Box>{inner}</Box> : <Box sx={{ p: 2 }}>{inner}</Box>;
});

export default ServicePartnerOnboardingSection;
